import React from 'react';
import CompActive from './CompActive';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className = '',
  onClick,
  type = 'button',
  'aria-label': ariaLabel,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center font-display font-semibold rounded-lg
    transition-all duration-300 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    hover:-translate-y-px active:translate-y-0
  `;

  const variants = {
    primary: `
      bg-primary hover:bg-primary-dark text-white
      focus:ring-primary focus:shadow-glow
      shadow-lg hover:shadow-primary/40
    `,
    secondary: `
      bg-secondary hover:bg-secondary-dark text-white
      focus:ring-secondary focus:shadow-[0_0_15px_0_rgba(245,158,11,0.3)]
      shadow-lg hover:shadow-secondary/40
    `,
    outline: `
      bg-transparent hover:bg-surface text-text-subtle border-2 border-border
      hover:text-text hover:border-primary focus:ring-primary
    `,
    ghost: `
      bg-transparent hover:bg-surface text-text-subtle hover:text-text
      focus:ring-primary
    `,
    danger: `
      bg-red-600 hover:bg-red-700 text-white
      focus:ring-red-500 shadow-lg hover:shadow-red-500/40
    `,
    // Novas variantes com cores distintas para cada ação
    watch: `
      bg-green-600 hover:bg-green-700 text-white
      focus:ring-green-500 focus:shadow-[0_0_15px_0_rgba(34,197,94,0.4)]
      shadow-lg hover:shadow-green-500/40
    `,
    subscribe: `
      bg-purple-600 hover:bg-purple-700 text-white
      focus:ring-purple-500 focus:shadow-[0_0_15px_0_rgba(147,51,234,0.4)]
      shadow-lg hover:shadow-purple-500/40
    `,
    buy: `
      bg-blue-600 hover:bg-blue-700 text-white
      focus:ring-blue-500 focus:shadow-[0_0_15px_0_rgba(59,130,246,0.4)]
      shadow-lg hover:shadow-blue-500/40
    `,
    rent: `
      bg-orange-600 hover:bg-orange-700 text-white
      focus:ring-orange-500 focus:shadow-[0_0_15px_0_rgba(249,115,22,0.4)]
      shadow-lg hover:shadow-orange-500/40
    `,
    ticket: `
      bg-pink-600 hover:bg-pink-700 text-white
      focus:ring-pink-500 focus:shadow-[0_0_15px_0_rgba(236,72,153,0.4)]
      shadow-lg hover:shadow-pink-500/40
    `,
    contribute: `
      bg-red-500 hover:bg-red-600 text-white
      focus:ring-red-400 focus:shadow-[0_0_15px_0_rgba(239,68,68,0.4)]
      shadow-lg hover:shadow-red-400/40
    `
  };

  const sizes = {
    sm: 'px-2 py-1 text-xm min-h-[32px]',
    md: 'px-3 py-1.5 text-sm min-h-[40px]',
    lg: 'px-4 py-2 text-base min-h-[48px]',
    xl: 'px-6 py-3 text-lg min-h-[56px]'
  };

  const combinedClasses = `
    ${baseClasses}
    ${variants[variant]}
    ${sizes[size]}
    ${className}
  `;

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      type={type}
      className={combinedClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <CompActive 
          size="sm" 
          color="white" 
          text=""
          className="mr-2" 
        />
      )}
      {children}
    </button>
  );
};

// Componente específico para link-buttons
export const LinkButton = ({
  children,
  variant = 'ghost',
  className = '',
  ...props
}) => {
  const linkClasses = `
    inline-flex items-center font-nunito font-medium text-sm text-text-subtle
    transition-colors duration-200 ease-in-out
    hover:text-primary focus:text-primary
    focus:outline-none focus:underline
    ${className}
  `;

  return (
    <button
      type="button"
      className={linkClasses}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button; 